<template>
    <div class="wrp-chat" :class="variant === 'modal' ? 'wrp-chat-modal' : ''">
        <div ref="messagesContainer" class="wrp-chat-messages">
            <div v-if="loading" class="wrp-chat-state">
                <font-awesome-icon :icon="['fas', 'spinner']" spin class="wrp-chat-state-icon" />
                <p>Loading conversation...</p>
            </div>
            <div v-else-if="messages.length === 0" class="wrp-chat-state">
                <div class="wrp-chat-state-icon-wrap">
                    <font-awesome-icon :icon="['fas', 'comment-dots']" class="wrp-chat-state-icon" />
                </div>
                <p class="wrp-chat-state-title">No messages yet</p>
                <p class="wrp-chat-state-sub">Send a reply to start the conversation.</p>
            </div>
            <template v-else>
                <div
                    v-for="msg in messages"
                    :key="msg.id"
                    class="wrp-chat-row"
                    :class="isOwnMessage(msg) ? 'wrp-chat-row-own' : 'wrp-chat-row-other'"
                >
                    <div class="wrp-chat-bubble" :class="isOwnMessage(msg) ? 'wrp-chat-bubble-own' : 'wrp-chat-bubble-other'">
                        <div class="wrp-chat-bubble-label">{{ messageLabel(msg) }}</div>
                        <div class="wrp-chat-bubble-text">{{ msg.message }}</div>
                        <div class="wrp-chat-bubble-time">{{ formatDateTime(msg.created_at) }}</div>
                    </div>
                </div>
            </template>
        </div>

        <form class="wrp-chat-composer" @submit.prevent="sendMessage">
            <div class="wrp-chat-composer-inner">
                <textarea
                    v-model="replyText"
                    rows="2"
                    placeholder="Write your reply..."
                    class="wrp-chat-input"
                    :disabled="sending"
                ></textarea>
                <button
                    type="submit"
                    class="wrp-chat-send"
                    :disabled="sending || !replyText.trim()"
                >
                    <font-awesome-icon v-if="sending" :icon="['fas', 'spinner']" spin />
                    <font-awesome-icon v-else :icon="['fas', 'paper-plane']" />
                    <span>{{ sending ? 'Sending' : 'Send' }}</span>
                </button>
            </div>
        </form>
    </div>
</template>

<script>
import gql from 'graphql-tag';

const GET_MESSAGES = gql`
  query GetWorkReadinessSubmissionMessages($submissionId: Int!) {
    getWorkReadinessSubmissionMessages(submission_id: $submissionId) {
      error
      message
      data {
        id
        message
        sender_type
        created_at
        user {
          id
          name
        }
      }
    }
  }
`;

const ADD_MESSAGE = gql`
  mutation AddWorkReadinessSubmissionMessage($submissionId: Int!, $message: String!) {
    addWorkReadinessSubmissionMessage(submission_id: $submissionId, message: $message) {
      error
      message
    }
  }
`;

export default {
    name: 'WorkReadinessSubmissionChat',
    props: {
        submissionId: {
            type: [Number, String],
            required: true,
        },
        studentName: {
            type: String,
            default: 'Student',
        },
        variant: {
            type: String,
            default: 'default',
        },
    },
    data() {
        return {
            messages: [],
            replyText: '',
            loading: false,
            sending: false,
        };
    },
    computed: {
        currentUserId() {
            const id = this.$store.getters.userId;
            return id ? parseInt(id, 10) : null;
        },
    },
    watch: {
        submissionId: {
            immediate: true,
            handler() {
                this.fetchMessages();
            },
        },
    },
    methods: {
        isOwnMessage(msg) {
            return msg && msg.user && this.currentUserId && parseInt(msg.user.id, 10) === this.currentUserId;
        },
        messageLabel(msg) {
            if (this.isOwnMessage(msg)) {
                return 'You';
            }
            if (msg.sender_type === 'student') {
                return this.studentName;
            }
            return msg.user && msg.user.name ? msg.user.name : 'Staff';
        },
        formatDateTime(dateStr) {
            if (!dateStr) return '';
            const options = { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
            return new Date(dateStr).toLocaleString(undefined, options);
        },
        scrollToBottom() {
            this.$nextTick(function () {
                const el = this.$refs.messagesContainer;
                if (el) {
                    el.scrollTop = el.scrollHeight;
                }
            }.bind(this));
        },
        async fetchMessages() {
            if (!this.submissionId) {
                this.messages = [];
                return;
            }
            this.loading = true;
            try {
                const { data } = await this.$apollo.query({
                    query: GET_MESSAGES,
                    variables: { submissionId: parseInt(this.submissionId, 10) },
                    fetchPolicy: 'network-only',
                });
                const res = data.getWorkReadinessSubmissionMessages;
                if (res.error) {
                    if (this.$swal) {
                        this.$swal.fire('Error', res.message, 'error');
                    }
                    this.messages = [];
                    return;
                }
                this.messages = res.data || [];
                this.scrollToBottom();
            } catch (err) {
                console.error('Failed to load messages:', err);
                this.messages = [];
            } finally {
                this.loading = false;
            }
        },
        async sendMessage() {
            const text = this.replyText.trim();
            if (!text || this.sending) return;
            this.sending = true;
            try {
                const { data } = await this.$apollo.mutate({
                    mutation: ADD_MESSAGE,
                    variables: {
                        submissionId: parseInt(this.submissionId, 10),
                        message: text,
                    },
                });
                const res = data.addWorkReadinessSubmissionMessage;
                if (res.error) {
                    if (this.$swal) {
                        this.$swal.fire('Error', res.message, 'error');
                    }
                    return;
                }
                this.replyText = '';
                await this.fetchMessages();
            } catch (err) {
                if (this.$swal) {
                    this.$swal.fire('Error', err.message || 'Failed to send message', 'error');
                }
            } finally {
                this.sending = false;
            }
        },
    },
};
</script>

<style scoped>
.wrp-chat {
    display: flex;
    flex-direction: column;
    border: 1px solid #e2e8f0;
    border-radius: 0.75rem;
    overflow: hidden;
    background: #f8fafc;
}

.wrp-chat-modal {
    border: none;
    border-radius: 0;
    background: transparent;
}

.wrp-chat-messages {
    min-height: 220px;
    max-height: 360px;
    overflow-y: auto;
    padding: 1rem 1.125rem;
    background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
}

.wrp-chat-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 2rem 1rem;
    color: #64748b;
    font-size: 0.875rem;
}

.wrp-chat-state-icon-wrap {
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    background: #e0e7ff;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.75rem;
}

.wrp-chat-state-icon {
    font-size: 1.25rem;
    color: #4f46e5;
}

.wrp-chat-state-title {
    margin: 0 0 0.25rem 0;
    font-weight: 600;
    color: #334155;
    font-size: 0.9375rem;
}

.wrp-chat-state-sub {
    margin: 0;
    font-size: 0.8125rem;
    color: #94a3b8;
}

.wrp-chat-row {
    display: flex;
    margin-bottom: 0.75rem;
}

.wrp-chat-row-own {
    justify-content: flex-end;
}

.wrp-chat-row-other {
    justify-content: flex-start;
}

.wrp-chat-bubble {
    max-width: 85%;
    padding: 0.625rem 0.875rem;
    border-radius: 1rem;
    font-size: 0.875rem;
    line-height: 1.45;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
}

.wrp-chat-bubble-own {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    color: #fff;
    border-bottom-right-radius: 0.25rem;
}

.wrp-chat-bubble-other {
    background: #fff;
    color: #1e293b;
    border: 1px solid #e2e8f0;
    border-left: 3px solid #6366f1;
    border-bottom-left-radius: 0.25rem;
}

.wrp-chat-bubble-label {
    font-size: 0.6875rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 0.25rem;
    opacity: 0.85;
}

.wrp-chat-bubble-other .wrp-chat-bubble-label {
    color: #6366f1;
    opacity: 1;
}

.wrp-chat-bubble-text {
    white-space: pre-wrap;
    word-break: break-word;
}

.wrp-chat-bubble-time {
    font-size: 0.6875rem;
    margin-top: 0.375rem;
    opacity: 0.75;
}

.wrp-chat-composer {
    border-top: 1px solid #e2e8f0;
    background: #fff;
    padding: 0.875rem 1rem;
}

.wrp-chat-composer-inner {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
}

@media (min-width: 480px) {
    .wrp-chat-composer-inner {
        flex-direction: row;
        align-items: flex-end;
    }
}

.wrp-chat-input {
    flex: 1;
    width: 100%;
    min-height: 2.75rem;
    padding: 0.625rem 0.875rem;
    font-size: 0.875rem;
    color: #1e293b;
    background: #f8fafc;
    border: 2px solid #e2e8f0;
    border-radius: 0.75rem;
    resize: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.wrp-chat-input:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
    background: #fff;
}

.wrp-chat-input:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.wrp-chat-send {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.625rem 1.25rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #fff;
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    border: none;
    border-radius: 0.75rem;
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(37, 99, 235, 0.35);
    transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
    flex-shrink: 0;
}

.wrp-chat-send:not(:disabled):hover {
    background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
    box-shadow: 0 6px 20px rgba(37, 99, 235, 0.45);
    transform: translateY(-1px);
}

.wrp-chat-send:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none;
}
</style>
